import { Field, useFormikContext } from "formik";
import React, { FC, useEffect, useRef, useState } from "react";
import { mapMask } from "../../utilities/cardFormatsUtil";
import { validateMask } from "../../utilities/Validation";
import Input from "./input";
import { useLocale } from "../../contexts/localeContext";

interface MaskProps {
    charactersPerField: number;
    numberOfFields: number;
    name: string;
    disabled?: boolean;
    setCurrentItem: React.SetStateAction<any>;
}

const MaskInput: FC<MaskProps> = ({ charactersPerField, name, numberOfFields, disabled = false, setCurrentItem }) => {
    const { values, setFieldValue, handleChange } = useFormikContext<any>();
    const [maskArray, setMaskArray] = useState<string[]>([]);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [onChangedTriggered, setOnChangedTriggered] = useState<boolean>(false);
    const {translate} = useLocale();
    
    useEffect(() => {
        if (!onChangedTriggered) {
            initializeMaskSubInput();
        }
    }, [values[name]])

    /**
     * Base on the number of input fields we create sub fields for the mask and initially these fields are empty
     * so we need to initialize them with the values from the main mask field. This should only be done once hence
     * the check for onChangedTriggered
     */
    const initializeMaskSubInput = () => {
        let subMaskFied = {};
        const maskValue = mapMask(values[name] ?? "");
        setMaskArray(maskValue);

        for (let index = 0; index < numberOfFields; index++) {
            if (maskValue[index]) {
                subMaskFied = { ...subMaskFied, [`${name}${index}`]: maskValue[index] };
            }
        }
        setCurrentItem((prevCurrentItem) => { return { ...prevCurrentItem, ...subMaskFied } });
    }

    const onChanged = (event, index: number) => {
        const value: string = event.target.value;
        maskArray[index] = value;
        setOnChangedTriggered(true);
        setFieldValue(name, maskArray.join(""));
        handleChange(event);

        if (value.length === 2 && index < 9) {
            inputRefs.current[index + 1]?.focus();
        }
    }

    const onKeyDown = (event, index: number) => {
        const value = event.target.value;

        if (event.key === 'Backspace' && value === '' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    }

    const onMaskValidation = (value) => {
        if (maskArray.length > 0) {
            return validateMask(maskArray, translate);
        }

        const maskValue = mapMask(value ?? "");
        return validateMask(maskValue, translate);
    }

    return <Field name={name} validate={onMaskValidation}>
        {({ field }) => {
            return <div className="flex flex-row">
                {
                    Array.from({ length: numberOfFields }).map((_, index) => {
                        return <Input
                            name={`${name}${index}`}
                            type="text"
                            key={index}
                            className="flex flex-row items-center p-1 border text-xs text-gray-800 w-10"
                            maxLength={charactersPerField}
                            onChange={(event) => onChanged(event, index)}
                            onKeyDown={(event) => onKeyDown(event, index)}
                            fieldStyle={{ textTransform: "uppercase", textAlign: "center" }}
                            innerRef={(element) => {
                                if (element) {
                                    inputRefs.current[index] = element;
                                }
                            }}
                            disabled={disabled} />
                    })
                }
            </div>
        }}
    </Field>
}

export default MaskInput;